<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
            </router-link>
            <div class="flex items-center gap-2">
              <span class="text-2xl">🎯</span>
              <span class="font-semibold text-purple-600">霍兰德职业兴趣测试</span>
            </div>
          </div>
          <button @click="goBack" class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
          </button>
        </div>

        <!-- 进度条 -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-gray-600">测试进度</span>
            <span class="text-sm font-medium text-psychology-primary">步骤 1/2</span>
          </div>
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-2">
            <div class="h-full bg-psychology-primary transition-all" style="width: 50%;"></div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-2xl mx-auto space-y-6">
        <!-- 页面标题 -->
        <div class="text-center">
          <div class="text-5xl mb-4">👤</div>
          <h1 class="text-2xl font-bold text-psychology-primary mb-2">基本信息</h1>
          <p class="text-gray-600">填写基本信息，获得更精准的分析结果</p>
        </div>

        <!-- 简化的表单 -->
        <div class="card p-8">
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- 昵称（可选） -->
            <div>
              <label for="nickname" class="block text-sm font-medium text-gray-700 mb-2">
                昵称 <span class="text-gray-400 text-xs">(可选)</span>
              </label>
              <input
                id="nickname"
                v-model="formData.nickname"
                type="text"
                placeholder="输入你的昵称"
                maxlength="20"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-psychology-primary focus:border-transparent transition-all"
              />
            </div>

            <!-- 性别 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">
                性别 <span class="text-red-500">*</span>
              </label>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  type="button"
                  @click="formData.gender = 'male'"
                  :class="[
                    'py-3 px-4 rounded-lg border-2 transition-all',
                    formData.gender === 'male'
                      ? 'border-psychology-primary bg-psychology-primary-10 text-psychology-primary font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-psychology-primary-20'
                  ]"
                >
                  👨 男
                </button>
                <button
                  type="button"
                  @click="formData.gender = 'female'"
                  :class="[
                    'py-3 px-4 rounded-lg border-2 transition-all',
                    formData.gender === 'female'
                      ? 'border-psychology-primary bg-psychology-primary-10 text-psychology-primary font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-psychology-primary-20'
                  ]"
                >
                  👩 女
                </button>
                <button
                  type="button"
                  @click="formData.gender = 'other'"
                  :class="[
                    'py-3 px-4 rounded-lg border-2 transition-all',
                    formData.gender === 'other'
                      ? 'border-psychology-primary bg-psychology-primary-10 text-psychology-primary font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-psychology-primary-20'
                  ]"
                >
                  🌈 其他
                </button>
                <button
                  type="button"
                  @click="formData.gender = 'prefer-not-to-say'"
                  :class="[
                    'py-3 px-4 rounded-lg border-2 transition-all text-sm',
                    formData.gender === 'prefer-not-to-say'
                      ? 'border-psychology-primary bg-psychology-primary-10 text-psychology-primary font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-psychology-primary-20'
                  ]"
                >
                  🤐 不愿透露
                </button>
              </div>
            </div>

            <!-- 年龄段 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">
                年龄段 <span class="text-red-500">*</span>
              </label>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  v-for="ageOption in ageOptions"
                  :key="ageOption.value"
                  type="button"
                  @click="formData.age = ageOption.value"
                  :class="[
                    'py-3 px-4 rounded-lg border-2 transition-all',
                    formData.age === ageOption.value
                      ? 'border-psychology-primary bg-psychology-primary-10 text-psychology-primary font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-psychology-primary-20'
                  ]"
                >
                  {{ ageOption.label }}
                </button>
              </div>
            </div>

            <!-- 提示信息 -->
            <div class="bg-psychology-primary-10 border border-psychology-primary-20 rounded-lg p-4">
              <div class="flex items-start gap-3">
                <span class="text-2xl">ℹ️</span>
                <div class="flex-1 text-sm text-gray-700">
                  <p class="font-medium mb-1">隐私保护</p>
                  <p class="text-gray-600">这些信息仅用于生成个性化的测试结果，所有数据仅存储在您的本地浏览器中，不会上传到任何服务器。</p>
                </div>
              </div>
            </div>

            <!-- 按钮组 -->
            <div class="flex gap-3">
              <button
                type="submit"
                :disabled="!isFormValid"
                :class="[
                  'flex-1 py-3 px-6 rounded-lg transition-all font-medium hover:shadow-lg flex items-center justify-center gap-2',
                  isFormValid
                    ? 'bg-psychology-primary text-white hover:bg-psychology-primary/90 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                <span>开始答题</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
            </div>
            <p v-if="!isFormValid" class="text-sm text-gray-500 text-center">
              请填写所有必填项(性别、年龄段)
            </p>
          </form>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />
  </div>
</template>

<script>
import BackToTop from '../components/BackToTop.vue'
import { createSession, getIncompleteSession } from '../utils/sessionStorage.js'

export default {
  name: 'BasicInfoPage',
  components: {
    BackToTop
  },
  data() {
    return {
      testType: '',
      formData: {
        nickname: '',
        gender: '',
        age: ''
      },
      ageOptions: [
        { value: 'under-18', label: '18岁以下' },
        { value: '18-22', label: '18-22岁' },
        { value: '23-27', label: '23-27岁' },
        { value: '28-32', label: '28-32岁' },
        { value: '33-37', label: '33-37岁' },
        { value: '38-42', label: '38-42岁' },
        { value: '43-47', label: '43-47岁' },
        { value: '48+', label: '48岁以上' }
      ]
    }
  },
  computed: {
    isFormValid() {
      return this.formData.gender && this.formData.age
    }
  },
  created() {
    // 获取测试类型
    this.testType = this.$route.query.testType || 'self'
    
    // 检查是否有未完成的会话
    const incompleteSession = getIncompleteSession()
    if (incompleteSession) {
      // 如果有未完成会话，提示用户并跳转回开始页面
      this.$toast?.warning('检测到未完成的测试，请先处理未完成的测试')
      this.$router.replace({
        path: '/assessment/start',
        query: {
          testType: this.testType
        }
      })
      return
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    handleSubmit() {
      // 验证表单
      if (!this.isFormValid) {
        this.$toast.warning('请填写所有必填项')
        return
      }

      // 创建新会话，包含测试类型和基本信息
      const session = createSession({
        testType: this.testType,
        userInfo: this.formData,
        answers: [],
        currentDimension: 0,
        currentQuestion: 0
      })
      
      console.log('Created new Holland session:', session.id, 'testType:', this.testType)
      
      // 跳转到问卷页面，传递sessionId
      this.$router.push({
        path: '/assessment/questions',
        query: { 
          sessionId: session.id
        }
      })
    }
  }
}
</script>

<style scoped>
.card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

input:focus {
  outline: none;
}

button[type="submit"]:hover {
  transform: translateY(-2px);
}

button[type="submit"]:active {
  transform: translateY(0);
}
</style>
